import { useEffect, useState } from 'react';
import { Plus, Copy, Trash2, Check, Pencil, ArrowDown, ArrowUp, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api, DAY_NAMES } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

export default function TemplateManager() {
    const [templates, setTemplates] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [days, setDays] = useState([]);
    const [selectedDayId, setSelectedDayId] = useState(null);
    const [routines, setRoutines] = useState([]);
    const [exercises, setExercises] = useState([]);
    const [newName, setNewName] = useState('');
    const [showAddDay, setShowAddDay] = useState(false);
    const [newDow, setNewDow] = useState(1);
    const [newDayName, setNewDayName] = useState('');
    const [addRoutineOpen, setAddRoutineOpen] = useState(false);
    const [routineForm, setRoutineForm] = useState({ exercise_id: '', sets: 3, reps_or_time: '10', rest_seconds: 60 });

    const loadAll = async () => {
        const [tRes, eRes] = await Promise.all([api.get('/templates'), api.get('/exercises')]);
        setTemplates(tRes.data);
        setExercises(eRes.data);
        const active = tRes.data.find((t) => t.is_active) || tRes.data[0];
        if (active) {
            setActiveId(active.id);
            await loadDays(active.id);
        }
    };
    const loadDays = async (tid) => {
        const r = await api.get(`/templates/${tid}/days`);
        setDays(r.data);
        if (r.data[0]) { setSelectedDayId(r.data[0].id); await loadRoutines(r.data[0].id); } else { setSelectedDayId(null); setRoutines([]); }
    };
    const loadRoutines = async (did) => {
        const r = await api.get(`/days/${did}/routines`);
        setRoutines(r.data);
    };
    useEffect(() => { loadAll(); }, []);

    const createTemplate = async () => {
        if (!newName.trim()) return;
        const r = await api.post('/templates', { name: newName });
        setNewName('');
        toast.success('Plantilla creada');
        setTemplates([...templates, r.data]);
    };
    const activate = async (t) => {
        await api.post(`/templates/${t.id}/activate`);
        toast.success(`"${t.name}" activada`);
        await loadAll();
    };
    const duplicate = async (t) => {
        const r = await api.post(`/templates/${t.id}/duplicate`);
        toast.success(`Duplicada: ${r.data.name}`);
        await loadAll();
    };
    const rename = async (t) => {
        const nv = window.prompt('Nuevo nombre', t.name);
        if (!nv) return;
        await api.put(`/templates/${t.id}`, { name: nv });
        await loadAll();
    };
    const deleteTpl = async (t) => {
        if (t.is_active) { toast.error('No puedes borrar la plantilla activa'); return; }
        if (!window.confirm(`Borrar plantilla "${t.name}"?`)) return;
        await api.delete(`/templates/${t.id}`);
        toast.success('Plantilla borrada');
        await loadAll();
    };

    const addDay = async () => {
        if (days.find((d) => d.day_of_week === newDow)) { toast.error('Ese día ya existe'); return; }
        await api.post(`/templates/${activeId}/days`, { id: 'tmp', template_id: activeId, day_of_week: newDow, name: newDayName || DAY_NAMES[newDow], total_duration_min: 30 });
        toast.success('Día añadido');
        setShowAddDay(false); setNewDayName('');
        await loadDays(activeId);
    };
    const deleteDay = async (d) => {
        if (!window.confirm(`Borrar día ${d.name || DAY_NAMES[d.day_of_week]} y todos sus ejercicios?`)) return;
        await api.delete(`/days/${d.id}`);
        toast.success('Día borrado');
        await loadDays(activeId);
    };

    const addRoutine = async () => {
        if (!routineForm.exercise_id) { toast.error('Selecciona un ejercicio'); return; }
        await api.post(`/days/${selectedDayId}/routines`, { ...routineForm, order_index: routines.length });
        toast.success('Ejercicio añadido');
        setAddRoutineOpen(false); setRoutineForm({ exercise_id: '', sets: 3, reps_or_time: '10', rest_seconds: 60 });
        await loadRoutines(selectedDayId);
    };
    const updateRoutine = async (r, patch) => {
        await api.put(`/routines/${r.id}`, { exercise_id: r.exercise_id, sets: r.sets, reps_or_time: r.reps_or_time, rest_seconds: r.rest_seconds, order_index: r.order_index, notes: r.notes || '', ...patch });
        await loadRoutines(selectedDayId);
    };
    const moveRoutine = async (r, dir) => {
        const i = routines.findIndex((x) => x.id === r.id);
        const j = i + dir;
        if (j < 0 || j >= routines.length) return;
        const other = routines[j];
        await updateRoutine(r, { order_index: other.order_index });
        await updateRoutine(other, { order_index: r.order_index });
    };
    const deleteRoutine = async (r) => {
        if (!window.confirm('Quitar ejercicio de este día?')) return;
        await api.delete(`/routines/${r.id}`);
        await loadRoutines(selectedDayId);
    };

    const currentTpl = templates.find((t) => t.id === activeId);

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1400px]">
            <header className="mb-6">
                <div className="text-xs text-muted-foreground uppercase tracking-widest">Gestor</div>
                <h1 className="text-3xl md:text-4xl font-display font-semibold">Plantillas de semana</h1>
                <p className="text-sm text-muted-foreground mt-1">Crea, edita y duplica rutinas para cada semana o ciclo.</p>
            </header>

            {/* Template list */}
            <div className="grid lg:grid-cols-3 gap-3 mb-6">
                {templates.map((t) => (
                    <Card key={t.id} className={`bg-card border p-4 ${t.is_active ? 'border-[hsl(var(--primary))]' : 'border-border'}`} data-testid={`template-card-${t.id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-display font-semibold">{t.name}</div>
                                {t.is_active && <Badge className="mt-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"><Check className="h-3 w-3 mr-1" /> Activa</Badge>}
                            </div>
                            <div className="flex gap-1">
                                {!t.is_active && <Button size="sm" variant="secondary" onClick={() => activate(t)} data-testid={`activate-template-${t.id}`}>Activar</Button>}
                                <Button size="sm" variant="ghost" onClick={() => duplicate(t)} title="Duplicar" data-testid={`duplicate-template-${t.id}`}><Copy className="h-4 w-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => rename(t)} title="Renombrar"><Pencil className="h-4 w-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteTpl(t)} title="Borrar" data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>
                        <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => { setActiveId(t.id); loadDays(t.id); }} data-testid={`edit-template-${t.id}`}>Editar días y ejercicios</Button>
                    </Card>
                ))}
                <Card className="bg-[hsl(var(--surface-2))] border-dashed border-border p-4 flex items-center gap-2">
                    <Input placeholder="Nueva plantilla (ej. Mesociclo fuerza)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-template-name" />
                    <Button onClick={createTemplate} data-testid="new-template-button"><Plus className="h-4 w-4" /></Button>
                </Card>
            </div>

            {/* Editing panel */}
            {currentTpl && (
                <Card className="bg-card border-border p-5">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-widest">Editando</div>
                            <div className="text-xl font-display font-semibold">{currentTpl.name}</div>
                        </div>
                        <Button variant="secondary" onClick={() => setShowAddDay(true)} data-testid="add-day-button"><Plus className="h-4 w-4 mr-1" /> Añadir día</Button>
                    </div>

                    {/* Day tabs */}
                    <div className="flex gap-2 flex-wrap mb-4">
                        {days.map((d) => (
                            <button key={d.id} onClick={() => { setSelectedDayId(d.id); loadRoutines(d.id); }}
                                className={`px-3 h-10 rounded-lg border text-sm ${selectedDayId === d.id ? 'border-[hsl(var(--primary))] bg-[hsl(var(--surface-2))]' : 'border-border bg-[hsl(var(--surface-2))]/50 text-muted-foreground'}`}
                                data-testid={`day-tab-${d.day_of_week}`}>
                                {d.name || DAY_NAMES[d.day_of_week]}
                            </button>
                        ))}
                        {days.length === 0 && <div className="text-sm text-muted-foreground">Aún no hay días. Añade uno.</div>}
                    </div>

                    {selectedDayId && (
                        <>
                            <div className="flex justify-between items-center mb-3">
                                <div className="text-sm text-muted-foreground">Ejercicios del día</div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => deleteDay(days.find((d) => d.id === selectedDayId))} data-testid="delete-day-button"><Trash2 className="h-4 w-4 mr-1" /> Borrar día</Button>
                                    <Button size="sm" onClick={() => setAddRoutineOpen(true)} data-testid="add-routine-button"><Plus className="h-4 w-4 mr-1" /> Añadir ejercicio</Button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {routines.map((r, i) => (
                                    <div key={r.id} className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-border" data-testid={`routine-row-${r.id}`}>
                                        <div className="h-12 w-12 rounded bg-[hsl(var(--surface-3))] grid place-items-center overflow-hidden flex-shrink-0">
                                            {r.exercise?.media_url ? <img src={r.exercise.media_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{r.exercise?.name}</div>
                                            <div className="flex gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
                                                <Input className="h-7 w-14 text-xs" value={r.sets} onChange={(e) => { const v = parseInt(e.target.value || '1', 10); updateRoutine(r, { sets: v }); }} data-testid={`routine-sets-${r.id}`} />
                                                <span>series</span>
                                                <Input className="h-7 w-20 text-xs" value={r.reps_or_time} onChange={(e) => updateRoutine(r, { reps_or_time: e.target.value })} data-testid={`routine-reps-${r.id}`} />
                                                <span>{r.exercise?.type === 'time' ? 'tiempo' : 'reps'}</span>
                                                <Input className="h-7 w-14 text-xs" value={r.rest_seconds} onChange={(e) => updateRoutine(r, { rest_seconds: parseInt(e.target.value || '0', 10) })} data-testid={`routine-rest-${r.id}`} />
                                                <span>s descanso</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => moveRoutine(r, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => moveRoutine(r, 1)} disabled={i === routines.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => deleteRoutine(r)} data-testid={`delete-routine-${r.id}`}><X className="h-4 w-4" /></Button>
                                        </div>
                                    </div>
                                ))}
                                {routines.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Sin ejercicios. Añade el primero.</div>}
                            </div>
                        </>
                    )}
                </Card>
            )}

            {/* Add day dialog */}
            <Dialog open={showAddDay} onOpenChange={setShowAddDay}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Añadir día</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Día de la semana</Label>
                            <Select value={String(newDow)} onValueChange={(v) => setNewDow(parseInt(v, 10))}>
                                <SelectTrigger data-testid="new-day-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5, 6, 7].map((d) => <SelectItem key={d} value={String(d)}>{DAY_NAMES[d]}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Nombre (opcional)</Label>
                            <Input value={newDayName} onChange={(e) => setNewDayName(e.target.value)} placeholder="Ej. Día de piernas" data-testid="new-day-name" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowAddDay(false)}>Cancelar</Button>
                        <Button onClick={addDay} data-testid="save-day-button">Añadir</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add routine dialog */}
            <Dialog open={addRoutineOpen} onOpenChange={setAddRoutineOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Añadir ejercicio al día</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Ejercicio</Label>
                            <Select value={routineForm.exercise_id} onValueChange={(v) => setRoutineForm({ ...routineForm, exercise_id: v })}>
                                <SelectTrigger data-testid="routine-exercise-select"><SelectValue placeholder="Selecciona un ejercicio" /></SelectTrigger>
                                <SelectContent>
                                    {exercises.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div><Label>Series</Label><Input type="number" value={routineForm.sets} onChange={(e) => setRoutineForm({ ...routineForm, sets: parseInt(e.target.value || '1', 10) })} data-testid="routine-form-sets" /></div>
                            <div><Label>Reps/Tiempo</Label><Input value={routineForm.reps_or_time} onChange={(e) => setRoutineForm({ ...routineForm, reps_or_time: e.target.value })} data-testid="routine-form-reps" /></div>
                            <div><Label>Descanso (s)</Label><Input type="number" value={routineForm.rest_seconds} onChange={(e) => setRoutineForm({ ...routineForm, rest_seconds: parseInt(e.target.value || '0', 10) })} data-testid="routine-form-rest" /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAddRoutineOpen(false)}>Cancelar</Button>
                        <Button onClick={addRoutine} data-testid="save-routine-button">Añadir</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
